import { IDataTableColumn, useGridState, useSearchApi, FieldSet, ButtonContainer } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { BreedJudgeApi, BreedJudgeGridDto, BreedJudgeSearch } from "../../../../../api";
import { ERoutes } from "../../../../../ERoutes";
import { CustomDataTable, AddButton, DeleteButton, ExportButton, EditButton } from "../../../../../components";
import { useGlobalData } from "../../../../../contexts";
import { useApiService, useTl, useDeleteApi } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { Intent, Dialog, Colors, Classes, ButtonGroup } from "@blueprintjs/core";
import { PersonBreedJudgePanelStack } from "./PersonBreedJudgePanelStack";

export interface IPersonBreedJudgePageProps {
  personId: number;
  editMode: boolean;
  onUpdated: (id) => void;
}

export const PersonBreedJudgePage: React.FunctionComponent<IPersonBreedJudgePageProps> = ({
  personId,
  editMode,
  onUpdated,
}) => {
  const api = useApiService(BreedJudgeApi);
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();
  const [sObjState, setsobjState] = React.useState<any>();

  const [dialogOpened, setDialogOpened] = React.useState(false);

  const tableState = useGridState({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5],
    pageSize: 5,
    sortKeys: { lastName: "ASC" },
  });

  const { totalCount } = tableState;

  const [selectedBreedJudgeId, setselectedBreedJudgeId] = React.useState<number>(0);

  const searchFunction = React.useCallback(
    (sObj: BreedJudgeSearch) => {
      const sobj = BreedJudgeSearch.fromJS({
        ...sObj,
        personId: personId,
        languageId: currentLanguage.languageId,
      });
      setsobjState(sobj);
      return api.baseSearch(sobj);
    },
    [api, currentLanguage.languageId, personId]
  );

  const { search, loading } = useSearchApi({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const { deleteItem } = useDeleteApi(
    React.useMemo(
      () => ({
        deleteApiFn: (data: number) => api.deleteBreedJudge(data),
        onDeletedFn: () => {
          onUpdated("PersonBreedJudgePage" + new Date().getTime());
          search();
        },
      }),
      [api, search, onUpdated]
    )
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        hidden: !editMode,
        render: (row: BreedJudgeGridDto) => (
          <ButtonContainer>
            <DeleteButton minimal={true} onDelete={() => deleteItem(row.breedJudgeId)} />
            <EditButton
              minimal={true}
              onClick={() => {
                setselectedBreedJudgeId(row.breedJudgeId);
                setDialogOpened(true);
              }}
            />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.BreedGroupLabel),
        fieldName: "breedGroupNumber",
        render: (row: BreedJudgeGridDto) => (
          <Link target="_blank" to={`${ERoutes.breedGroups}/${row.breedGroupId}/detail/view`}>
            {row.breedGroupFullName}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Breed),
        fieldName: "breedName",
        render: (row: BreedJudgeGridDto) => (
          <Link target="_blank" to={`${ERoutes.breeds}/${row.breedId}/detail/view`}>
            {row.breedName}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.JudgeType),
        fieldName: "judgeType",
      },
      {
        header: () => t(ETLCodes.JudgeDateNomination),
        fieldName: "nominationDate",
      },
      {
        header: () => t(ETLCodes.JudgeDateInternship),
        fieldName: "internshipDate",
      },
      {
        header: () => t(ETLCodes.JudgeDateExcludedFrom),
        fieldName: "excludedFrom",
      },
      {
        header: () => t(ETLCodes.JudgeDateExcludedTo),
        fieldName: "excludedTo",
      },
    ],
    [deleteItem, editMode, t]
  );

  const addItemFunction = React.useCallback(() => {
    setDialogOpened(true);
  }, []);
  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);

  return (
    <FieldSet
      title={t(ETLCodes.Breeds, { totalCount: totalCount })}
      rightElement={
        <ButtonGroup>
          <AddButton
            onClick={(e) => {
              e.stopPropagation();
              addItemFunction();
            }}
            text={t(ETLCodes.General_Add)}
            intent={Intent.PRIMARY}
          />
          <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sObjState} />
        </ButtonGroup>
      }
    >
      <CustomDataTable tableState={tableState} loading={loading} columns={columns} />

      <Dialog
        title={t(ETLCodes.Breeds)}
        isOpen={dialogOpened}
        onClose={() => {
          setDialogOpened(false);
          setselectedBreedJudgeId(null);
        }}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        style={{ width: 1000, height: 750, background: Colors.WHITE }}
        className={Classes.DIALOG}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 200 }}>
          <PersonBreedJudgePanelStack
            personId={personId}
            breedJudgeId={selectedBreedJudgeId}
            onSaved={() => {
              setDialogOpened(false);
              onUpdated("PersonBreedJudgePage" + new Date().getTime());
              search();
            }}
          />
        </div>
      </Dialog>
    </FieldSet>
  );
};
